import axios from 'axios'
import store from '@/store'
import router from '@/router'

// Create axios instance for general API calls
const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'https://yoyevu-api.yoyevu.co/api/',
  timeout: 120000, // 2 minutes for data-heavy requests like getMetricData
  headers: {
    'Content-Type': 'application/json'
  }
})

// Create axios instance for authentication
const openApi = axios.create({
  baseURL: process.env.VUE_APP_API_AUTH_URL || 'http://yoyevu-api.test/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor for general API
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      // API Request: No token found
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor for general API
api.interceptors.response.use(
  response => {
    return response
  },
  error => {
    const originalRequest = error.config

    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Clear auth state and redirect to login
      store.dispatch('auth/logout')
      router.push('/login')
    }

    // Handle 403 Forbidden
    if (error.response && error.response.status === 403) {
      store.dispatch('setError', 'No tienes permisos para realizar esta acción')
    }

    // Handle 500 Server Error
    if (error.response && error.response.status >= 500) {
      store.dispatch('setError', 'Error del servidor. Por favor, intenta más tarde.')
    }

    return Promise.reject(error)
  }
)

// API endpoints
export const authAPI = {
  login: credentials => openApi.post('/auth', credentials),
  register: userData => openApi.post('/register', userData),
  logout: () => openApi.post('/logout'),
  me: () => api.get('/auth/me'), // This uses the general API since it requires auth
  updateProfile: profileData => api.put('/auth/profile', profileData),
  changePassword: passwordData => api.put('/auth/password', passwordData),
  forgotPassword: email => openApi.post('/forgot-password', { email }),
  resetPassword: (token, password) => openApi.post('/reset-password', { token, password })
}

export const sensorsAPI = {
  getAll: () => api.get('/sensors'),
  getById: id => api.get(`/sensors/${id}`),
  create: sensorData => api.post('/sensors', sensorData),
  update: (id, sensorData) => api.put(`/sensors/${id}`, sensorData),
  delete: id => api.delete(`/sensors/${id}`),
  getReadings: (sensorId, params) => api.get(`/sensors/${sensorId}/readings`, { params }),
  getLatestReadings: sensorIds => api.get('/sensors/readings/latest', { params: { sensorIds } })
}

export const devicesAPI = {
  getAll: (params) => api.get('/iot/devices', { params }),
  getById: id => api.get(`/iot/devices/${id}`),
  create: deviceData => api.post('/iot/devices', deviceData),
  update: (id, deviceData) => api.put(`/iot/devices/${id}`, deviceData),
  delete: id => api.delete(`/iot/devices/${id}`),
  getSensors: deviceId => api.get(`/iot/devices/${deviceId}/sensors`),
  getStatus: deviceId => api.get(`/iot/devices/${deviceId}/status`),
  getRealTimeData: deviceId => api.get(`/iot/devices/${deviceId}/realtime`),
  getLocation: deviceId => api.get(`/iot/devices/${deviceId}/location`),
  getHistory: (deviceId, params) => api.get(`/iot/devices/${deviceId}/history`, { params }),
  enable: deviceId => api.post(`/iot/devices/${deviceId}/enable`),
  disable: deviceId => api.post(`/iot/devices/${deviceId}/disable`),
  sync: deviceId => api.post(`/iot/devices/${deviceId}/sync`)
}

export const alertsAPI = {
  getAll: () => api.get('/alerts'),
  getById: id => api.get(`/alerts/${id}`),
  create: alertData => api.post('/alerts', alertData),
  update: (id, alertData) => api.put(`/alerts/${id}`, alertData),
  delete: id => api.delete(`/alerts/${id}`),
  acknowledge: id => api.post(`/alerts/${id}/acknowledge`),
  getActive: () => api.get('/alerts/active'),
  getHistory: params => api.get('/alerts/history', { params })
}

export const reportsAPI = {
  getTemperatureReport: params => api.get('/reports/temperature', { params }),
  getHumidityReport: params => api.get('/reports/humidity', { params }),
  getAlertsReport: params => api.get('/reports/alerts', { params }),
  getDeviceReport: deviceId => api.get(`/reports/devices/${deviceId}`),
  exportReport: (type, params) => api.get(`/reports/export/${type}`, { params, responseType: 'blob' })
}

export const iotAPI = {
  getMetrics: params => api.get('/iot/metrics', { params }),
  getMetricData: (metricId, params) => api.get(`/iot/metrics/${metricId}/full-history`, { 
    params,
    timeout: 180000 // 3 minutes for heavy data requests
  }),
  searchDevices: (term) => api.get('/iot/devices', { 
    params: { 
      'filter-by': 'name', 
      term: term 
    } 
  })
}

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/activity'),
  getSystemStatus: () => api.get('/dashboard/status'),
  getRealTimeData: () => api.get('/dashboard/realtime')
}

export default api 


// WEBPACK FOOTER //
// ./src/api/index.js